import { useRouter } from 'next/router';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Optional } from '@common/type/CommonTypes';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';

import CourseReviewActionButton from './courseReviewActionButton';
import CourseReviewItem from './courseReviewItem';
import CreateOrUpdateCourseReviewDialog from './createOrUpdateCourseReviewDialog';

import Pagination from '@components/share/navigation/pagination';
import { useCourseReview } from '@hooks/course/courseReview/useCourseReview';
import { COURSE_REVIEW_LIST_PAGE_SIZE } from '@hooks/course/courseReview/useCourseReviewListing';

type CourseReviewListProps = {
  handleFetchCourseReview: () => Promise<void>;
};

const CourseReviewList: FC<CourseReviewListProps> = ({ handleFetchCourseReview }) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const router = useRouter();
  const idName = router.query.idName as Optional<string>;

  const { enqueueSnackbar } = useSnackbar();

  const {
    handleFetchCourseReviewList,
    courseReviewListPage,
    courseReviewListData,
    isCreateOrUpdateCourseReviewDataSuccess,
    isCreateOrUpdateCourseReviewDataError,
    courseReviewMeData,
  } = useCourseReview({ idName });

  const reviews = courseReviewListData?.reviews || [];
  const reviewTotal = courseReviewListData?.count ?? 0;

  const [isOpenCreateOrUpdateCourseReviewDialog, setIsOpenCreateOrUpdateCourseReviewDialog] = useState<boolean>(false);

  const isUpdateReview = useMemo(() => {
    return !!courseReviewMeData;
  }, [courseReviewMeData]);

  const pageCount = useMemo(() => {
    return Math.ceil(reviewTotal / COURSE_REVIEW_LIST_PAGE_SIZE);
  }, [reviewTotal]);

  useEffect(() => {
    if (isCreateOrUpdateCourseReviewDataSuccess) {
      enqueueSnackbar({
        message: isUpdateReview
          ? t('course_detail.course_review.update_review.success')
          : t('course_detail.course_review.create_review.success'),
        severity: 'success',
        isHideCloseIcon: true,
      });
      setIsOpenCreateOrUpdateCourseReviewDialog(false);
      handleFetchCourseReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, handleFetchCourseReview, isCreateOrUpdateCourseReviewDataSuccess]);

  useEffect(() => {
    if (isCreateOrUpdateCourseReviewDataError) {
      enqueueSnackbar({
        message: t('course_detail.course_review.create_or_update_review.failed'),
        severity: 'error',
        isHideCloseIcon: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueSnackbar, isCreateOrUpdateCourseReviewDataError]);

  const handleChangePagination = useCallback(
    (_event: ChangeEvent<unknown>, nextPage: number) => {
      if (courseReviewListPage !== nextPage) {
        handleFetchCourseReviewList({ page: nextPage });
      }
    },
    [courseReviewListPage, handleFetchCourseReviewList],
  );

  return (
    <>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" display="flex" gap={2}>
            {t('course_detail.course_review.all_review')}

            <Typography variant="BaseLegacy" color={theme.palette.textV2.secondary}>
              ({t('course_detail.course_review.number_review', { review: courseReviewListData?.count ?? 0 })})
            </Typography>
          </Typography>

          <CourseReviewActionButton
            isUpdateReview={isUpdateReview}
            setIsOpenCreateOrUpdateCourseReviewDialog={setIsOpenCreateOrUpdateCourseReviewDialog}
          />
        </Box>

        {reviews.map((review, index) => (
          <CourseReviewItem key={review.id} review={review} isUnderline={reviews.length !== index + 1} />
        ))}

        {pageCount > 1 ? (
          <Box
            sx={{
              mt: 1,
              mb: 3,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination page={courseReviewListPage} count={pageCount} onChange={handleChangePagination} size="large" />
          </Box>
        ) : null}
      </Box>

      <CreateOrUpdateCourseReviewDialog
        isOpen={isOpenCreateOrUpdateCourseReviewDialog}
        setIsOpen={setIsOpenCreateOrUpdateCourseReviewDialog}
      />
    </>
  );
};

export default CourseReviewList;
